import { IconInfo, Tooltip } from '@leland-dev/leland-ui-library';
import { useRouter } from 'next/router';
import React, { type ReactElement, useEffect } from 'react';

import { useAuth } from '../context/AuthContext';
import { BASE_SEARCH_PATH } from '../utils/search';
import { redirectToLogin } from '../utils/url';

import LoadingIndicator from './common/LoadingIndicator';
import Page from './page/Page';
import PageContent from './page/PageContent';
import SettingsNavigationSidebar from './settings/SettingsNavigationSidebar';

interface Props
  extends Pick<
    React.ComponentPropsWithoutRef<typeof PageContent>,
    'initialData'
  > {
  title: string;
  titleClassName?: string;
  titleTooltip?: string;
  secondaryTitle?: string;
  secondaryTitleClassName?: string;
  showProfileLink?: boolean;
  enforceOps?: boolean;
  rightContent?: ReactElement;
  narrowWidth?: boolean;
  fullWidth?: boolean;
  hideTitle?: boolean;
}

const AuthedPageBase: React.FC<React.PropsWithChildren<Props>> = ({
  title,
  titleTooltip,
  initialData,
  ...props
}) => {
  const { currentUser, isLoadingUser } = useAuth();
  const router = useRouter();

  useEffect(() => {
    if (isLoadingUser) {
      return;
    }

    if (currentUser == null) {
      void redirectToLogin(router);
    } else if (currentUser.applicant == null) {
      void router.replace(BASE_SEARCH_PATH);
    }
  }, [currentUser, isLoadingUser, router]);

  if (isLoadingUser) {
    return (
      <Page title={`Loading ${title}`}>
        <PageContent hideSubnav initialData={initialData}>
          <LoadingIndicator
            className="min-h-screen"
            iconClassName="w-20 h-20"
            text="Loading user details..."
          />
        </PageContent>
      </Page>
    );
  }

  if (currentUser?.applicant == null) {
    return null;
  }

  return (
    <Page title={title}>
      <PageContent initialData={initialData} hideSubnav>
        <div className="flex grow">
          <SettingsNavigationSidebar />

          <div
            className={`mx-auto w-full grow ${props.fullWidth ? '' : 'p-4 lg:py-8'} ${
              props.narrowWidth
                ? 'max-w-page-narrow'
                : props.fullWidth
                  ? ''
                  : 'max-w-page'
            }`}
          >
            <div className="flex flex-row flex-wrap items-start justify-between">
              {props.hideTitle ? null : (
                <div className="mb-2">
                  <div className="flex flex-row items-center">
                    <h1
                      className={`text-3xl font-medium ${
                        props.titleClassName ?? ''
                      }`}
                    >
                      {title}
                    </h1>
                    {titleTooltip ? (
                      <Tooltip content={titleTooltip}>
                        <IconInfo className="ml-2 size-4 text-leland-gray-extra-light" />
                      </Tooltip>
                    ) : null}
                  </div>
                  {props.secondaryTitle != null ? (
                    <p
                      className={`font-normal text-leland-gray-light ${
                        props.secondaryTitleClassName ?? ''
                      }`}
                    >
                      {props.secondaryTitle}
                    </p>
                  ) : null}
                </div>
              )}
              {props.rightContent != null && props.rightContent}
            </div>
            {props.children}
          </div>
        </div>
      </PageContent>
    </Page>
  );
};

export default AuthedPageBase;
